import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { Input } from "components/form_components/Inputs";
import Button from "components/ui/button/Button";
import Modal from "components/ui/modal/Modal";
import toast from "components/ui/toast/Toast";
import { CreateFolderRequest_DocumentTypeEnum_5 } from "lib/generated/app-service-gql/graphql";
import { useCreateContentLibraryFolder } from "lib/hooks/api/customer_documents/useCreateContentLibraryFolder";

type Props = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  uiPath: string | undefined; // Only present if we're already in a folder
};
export function CreateContentLibraryFolderModal({ isOpen, onOpenChange, uiPath }: Props) {
  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      // Only use the base name for the input
      folderName: "",
    },
    mode: "onChange", // validates as you type
  });

  const onOpenModalChange = (open: boolean) => {
    onOpenChange(open);
    if (!open) {
      reset(); // Reset folderName when modal is closed
    }
  };
  const { mutate: createFolder, isLoading } = useCreateContentLibraryFolder({
    onSuccess: (data) => {
      toast.success(`Created folder ${data.createContentLibraryFolder.name}`);
      onOpenChange(false); // Close modal on success
    },
    onError: (_err, vars) => toast.error(`Failed to create folder ${vars.request.name}`),
  });

  return (
    <Modal open={isOpen} showCloseButton={false} onOpenChange={onOpenModalChange}>
      <Form
        aria-label="New folder"
        onSubmit={handleSubmit(({ folderName }) => {
          createFolder({
            request: {
              documentType: CreateFolderRequest_DocumentTypeEnum_5.ContentLibrary,
              name: folderName.trim(),
              uiPath,
            },
          });
        })}
      >
        <Flex flexDirection="column" gap={4}>
          <Input
            width="100%"
            name="folderName"
            control={control}
            placeholder="Untitled folder"
            label={<Label>New folder</Label>}
            rules={{
              required: "Folder name is required",
              pattern: {
                value: /^(?!.*[<>:"/\\|?*])[^. ](.*[^. ])?$/, // Disallow invalid characters
                message:
                  'Folder name contains invalid characters (e.g. / \\ : * ? " < > |), or starts/ends with a space or dot',
              },
            }}
          />
        </Flex>
        <Flex gap={8} alignSelf="flex-end">
          <Button
            buttonType="secondary"
            disabled={isLoading}
            onClick={() => onOpenModalChange(false)}
          >
            Cancel
          </Button>
          <Button type="submit" loading={isLoading}>
            <Text color="white">Add</Text>
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
}

const Form = styled.form({
  display: "flex",
  flexDirection: "column",
  gap: 24,
  minWidth: "400px",
});

const Label = styled(Text)({
  fontWeight: 500,
  marginBottom: 4,
  fontSize: 16,
});
